import { useMemo } from 'react'
import { Program } from '@citruscamps/citrus-client'
import { DefaultSinglePagination } from '../../../../../constants/pagination'
import { useLocalStorage } from '../../../../../hooks/useLocalStorage'
import { useFetchEvents } from '../../../../Events/pages/ManageEvents/hooks/useFetchEvents'
import { useFetchMembers } from '../../../../Team/pages/ManageMembers/hooks/useFetchMembers'
import { useFetchPayoutSettings } from '../../../../Settings/pages/PaymentSettings/hooks/useFetchPayoutSettings'
import { useFlags } from '../../../../../components/FeatureFlag/hooks/useFlags'
import { DateTime } from 'luxon'

export type OnboardingStep = 'team' | 'payout' | 'camps' | 'branding'
export type ExtendedOnboardingStep = OnboardingStep | 'referral' | 'billing'

interface IProps {
  program?: Program
}

interface FetchOnboarding {
  onboarding?: OnboardingStep[]
  isLoading: boolean
  isRecommendedToUpgradePlan: boolean
  isRecommendedToReferral: boolean
  handleSkip: (value: ExtendedOnboardingStep) => void
  handleSetupAccount: () => void
}
export const useFetchOnboarding = ({ program }: IProps): FetchOnboarding => {
  const { billing } = program || {}
  const { flags } = useFlags()
  const { isLoading: isLoadingMembers, pagination: teamPagination } = useFetchMembers({
    programId: program?.id,
    pagination: DefaultSinglePagination,
  })
  const { handleSetupAccount } = useFetchPayoutSettings({
    programId: program?.id,
  })
  const { isLoading: isLoadingEvents, pagination: eventPagination } = useFetchEvents({
    programId: program?.id,
    pagination: { limit: 1, count: 0, total: 0, page: 0 },
  })
  const [skippedOnboarding, setSkippedOnboarding] = useLocalStorage<OnboardingStep[]>(
    `skipped-onboarding-${program?.id}`,
    [],
  )
  const [isUpgradePlanIgnored, setIsUpgradePlanIgnored] = useLocalStorage<boolean>(
    `skipped-upgrade-plan-${program?.id}`,
    false,
  )
  const [isReferralIgnored, setIsReferralIgnored] = useLocalStorage<boolean>(
    `skipped-referral-${program?.id}`,
    false,
  )
  const handleSkip = (step: ExtendedOnboardingStep) => {
    if (step === 'billing') {
      setIsUpgradePlanIgnored(true)
      return
    }
    if (step === 'referral') {
      setIsReferralIgnored(true)
      return
    }
    const skipped = [...(skippedOnboarding || []), step]
    setSkippedOnboarding(skipped)
  }

  const onboarding = useMemo(() => {
    if (!isLoadingEvents && !isLoadingMembers && program) {
      const requiresOnboarding: OnboardingStep[] = []
      if (eventPagination.total < 1) {
        requiresOnboarding.push('camps')
      }
      const isMissingBranding =
        !program.preferences?.brand_accent_color ||
        !program.preferences?.icon ||
        !program.preferences?.logo
      if (isMissingBranding) {
        requiresOnboarding.push('branding')
      }
      if (teamPagination.total <= 1) {
        requiresOnboarding.push('team')
      }
      if (!flags?.online_payments_enabled) {
        requiresOnboarding.push('payout')
      }
      return requiresOnboarding.filter(
        (i) => !(skippedOnboarding || [])?.includes(i) || i === 'payout',
      )
    }
  }, [
    eventPagination.total,
    isLoadingEvents,
    isLoadingMembers,
    program,
    flags,
    skippedOnboarding,
    teamPagination.total,
  ])

  const today = DateTime.local()
  const isProgram30DaysOld =
    !!program && DateTime.fromJSDate(program.created) <= today.minus({ days: 30 })
  const isProgram60DaysOld =
    !!program && DateTime.fromJSDate(program.created) <= today.minus({ days: 60 })
  const isRecommendedToUpgradePlan =
    (!billing ||
      !billing?.products.some(
        (product) => product.type === 'pro' || product.type === 'enterprise',
      )) &&
    isProgram30DaysOld &&
    !isUpgradePlanIgnored
  const isRecommendedToReferral = isProgram60DaysOld && !isReferralIgnored

  return {
    onboarding,
    isLoading: !program || isLoadingMembers || isLoadingEvents,
    isRecommendedToUpgradePlan,
    isRecommendedToReferral,
    handleSkip,
    handleSetupAccount,
  }
}
